/**
 * Result fetching + CSV parsing.
 *
 * Extracted out of Pages/Home.jsx so the home page, the chart page and the
 * per-game result pages all share one implementation (and one fetch cache).
 */

export const MONTH_NAME_MAP = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

export const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SHEETS_CACHE = {
  data: new Map(),
  timestamps: new Map(),
  maxAge: 5 * 60 * 1000,
};

export function formatMonthYear(monthIndex, year) {
  const name = MONTH_LABELS[monthIndex] || "";
  return `${name} ${year}`;
}

export function getNowIST() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utc + 5.5 * 60 * 60 * 1000);
}

export function getDateInfoIST(nowIST) {
  const today = {
    day: nowIST.getDate(),
    month: nowIST.getMonth() + 1,
    year: nowIST.getFullYear(),
  };

  const y = new Date(nowIST);
  y.setDate(y.getDate() - 1);
  const yesterday = {
    day: y.getDate(),
    month: y.getMonth() + 1,
    year: y.getFullYear(),
  };

  return {
    today,
    yesterday,
    currentMonthNum: today.month,
    currentYear: today.year,
  };
}

export function getDaysInMonth(year, monthNum1) {
  return new Date(year, monthNum1, 0).getDate();
}

export function formatISTDate(date) {
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** YYYY-MM-DD in IST — used for schema.org dates and sitemap lastmod. */
export function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function pad2(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  if (/^-?\d+$/.test(s)) {
    const n = Number(s);
    if (n >= 0 && n < 10) return "0" + n;
  }
  return s;
}

export function pad2OrDash(value) {
  if (value === "-" || value == null || value === "") return "XX";
  return pad2(value);
}

async function fetchSheetCsv(sheetId) {
  const now = Date.now();
  if (SHEETS_CACHE.data.has(sheetId)) {
    const ts = SHEETS_CACHE.timestamps.get(sheetId);
    if (now - ts < SHEETS_CACHE.maxAge) {
      return SHEETS_CACHE.data.get(sheetId);
    }
  }

  const url = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&_=${Date.now()}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error("Failed to fetch sheet CSV");
  }
  const csv = await resp.text();
  SHEETS_CACHE.data.set(sheetId, csv);
  SHEETS_CACHE.timestamps.set(sheetId, now);
  return csv;
}

export function parseCsvForGame(csv, time24, sheetYear, dateOverride) {
  const nowIST = dateOverride || getNowIST();
  const { today, yesterday, currentYear } = getDateInfoIST(nowIST);

  const targetYear = sheetYear || currentYear;

  const lines = csv
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    return {
      today: null,
      yesterday: null,
      shouldShowToday: false,
      currentYear,
      monthDataByMonth: {},
    };
  }

  const headerCells = lines[0].split(",");
  const monthHeaders = headerCells.slice(1);

  let todayResult = null;
  let yesterdayResult = null;
  const monthDataByMonth = {};

  Object.values(MONTH_NAME_MAP).forEach((m) => {
    monthDataByMonth[m] = {};
  });

  for (let i = 1; i < lines.length; i += 1) {
    const rowCells = lines[i].split(",");
    const dayRaw = rowCells[0]?.trim();
    const day = parseInt(dayRaw, 10);
    if (!Number.isFinite(day)) continue;

    for (let mi = 0; mi < monthHeaders.length; mi += 1) {
      const monthHeader = monthHeaders[mi]?.trim();
      const monthNum = MONTH_NAME_MAP[monthHeader];
      if (!monthNum) continue;

      const cell = (rowCells[mi + 1] || "").trim();
      if (!cell || cell.toLowerCase() === "null" || cell === "-") {
        continue;
      }

      monthDataByMonth[monthNum][day] = cell;

      if (day === today.day && monthNum === today.month) {
        todayResult = cell;
      }
      if (day === yesterday.day && monthNum === yesterday.month) {
        yesterdayResult = cell;
      }

      if (
        today.day === 1 &&
        monthNum === yesterday.month &&
        day === yesterday.day
      ) {
        yesterdayResult = cell;
      }
    }
  }

  let shouldShowToday = true;
  if (todayResult !== null && time24) {
    if (targetYear === today.year) {
      const [hh, mm] = time24.split(":").map(Number);
      const scheduledUtc = new Date(
        Date.UTC(today.year, today.month - 1, today.day, hh - 5, mm - 30, 0)
      );
      const nowUtc = new Date();
      if (nowUtc.getTime() < scheduledUtc.getTime()) {
        shouldShowToday = false;
        todayResult = null;
        if (monthDataByMonth[today.month]) {
          delete monthDataByMonth[today.month][today.day];
        }
      }
    }
  }

  return {
    today: todayResult,
    yesterday: yesterdayResult,
    shouldShowToday,
    currentYear,
    monthDataByMonth,
  };
}

export async function fetchGameResultForLive(gameConfig) {
  const nowIST = getNowIST();
  const currentYear = nowIST.getFullYear();
  const byYear = gameConfig.sheetIdsByYear || {};
  const sheetId = byYear[currentYear] || gameConfig.sheetId;
  const csv = await fetchSheetCsv(sheetId);
  const result = parseCsvForGame(csv, gameConfig.time24, currentYear);

  const { yesterday } = getDateInfoIST(nowIST);
  if (yesterday.year < currentYear) {
    const prevYear = yesterday.year;
    const prevSheetId = byYear[prevYear];
    if (prevSheetId) {
      try {
        const prevCsv = await fetchSheetCsv(prevSheetId);
        const prevDate = new Date(
          yesterday.year,
          yesterday.month - 1,
          yesterday.day,
          12,
          0,
          0
        );
        const prevResult = parseCsvForGame(
          prevCsv,
          gameConfig.time24,
          prevYear,
          prevDate
        );
        if (prevResult.today !== null) {
          result.yesterday = prevResult.today;
        }
      } catch (err) {
        console.error(
          "Failed to fetch previous year data for yesterday result",
          err
        );
      }
    }
  }

  return result;
}

export async function fetchGameResultForYear(gameConfig, year) {
  const byYear = gameConfig.sheetIdsByYear || {};
  const sheetId = byYear[year] || gameConfig.sheetId;
  const csv = await fetchSheetCsv(sheetId);
  return parseCsvForGame(csv, gameConfig.time24, year);
}
